import { ReactNode, useEffect, useId, useState } from "react";
import type { SimulatorProduct, SimulatorResult, SimulatorRequest } from "common/types/Simulator";
import Typography, { ITypo, ITypoComponent } from "components/elements/Typography";
import MuiTable from "components/elements/Table/MuiTable";
import NavTabComponent from "components/elements/NavTabComponent";
import MessageBox from "components/elements/MessageBox";
import TooltipElement from "components/elements/TooltipElement";
import { money, percent } from "./controls";
import { ownershipLabels } from "./labels";
import { exposureNote, feesNote, projectionDisclaimer } from "./texts";
import classes from "./classes.module.scss";

export default function Results({
	result: r,
	products,
	taxEnabled,
	ownership,
	creditEnabled,
	actions,
}: {
	result: SimulatorResult;
	products: SimulatorProduct[];
	taxEnabled: boolean;
	ownership: SimulatorRequest["ownership"];
	creditEnabled: boolean;
	actions?: ReactNode;
}) {
	const gradientId = useId();
	const [chartElement, setChartElement] = useState<SVGSVGElement | null>(null);
	const [chartWidth, setChartWidth] = useState(760);
	const [activeYear, setActiveYear] = useState(0);
	const [tooltipOpen, setTooltipOpen] = useState(false);
	useEffect(() => {
		setTooltipOpen(false);
		setActiveYear(0);
	}, [r, chartElement]);
	useEffect(() => {
		if (!chartElement) return;
		const observer = new ResizeObserver(([entry]) => {
			if (entry) setChartWidth(Math.max(250, entry.contentRect.width));
		});
		observer.observe(chartElement);
		return () => observer.disconnect();
	}, [chartElement]);
	const first = r.years[0]!;
	const feeRate = (rate: number | null, subscription: boolean) => (rate === null ? "Non renseigné" : percent(subscription ? rate / 100 : rate));
	const feeAmount = (amount: number | null) => (amount === null ? "Non renseigné" : money(amount));
	const last = r.years[r.years.length - 1]!;
	const maxAsset = Math.max(1, ...r.years.flatMap((y) => [y.asset, y.debt]));
	const yearIndex = Math.min(activeYear, r.years.length - 1);
	const highlighted = r.years[yearIndex]!;
	const highlightedX = 70 + (yearIndex / Math.max(1, r.years.length - 1)) * (chartWidth - 90);
	const selectAtPointer = (clientX: number, clientY: number) => {
		const matrix = chartElement?.getScreenCTM();
		if (!matrix) return;
		const point = new DOMPoint(clientX, clientY).matrixTransform(matrix.inverse());
		setActiveYear(Math.max(0, Math.min(r.years.length - 1, Math.round(((point.x - 70) / (chartWidth - 90)) * (r.years.length - 1)))));
	};
	const points = (key: "asset" | "debt") =>
		r.years.map((y, i) => `${70 + (i / Math.max(1, r.years.length - 1)) * (chartWidth - 90)},${145 - (y[key] / maxAsset) * 110}`).join(" ");
	return (
		<div className={classes["results"]}>
			<div className={classes["resultHeading"]}>
				<div>
					<h2>Vue d’ensemble</h2>
					<p>
						{products.length} SCPI sélectionnée{products.length > 1 ? "s" : ""} · Projection sur {r.years.length} ans
					</p>
				</div>
				<div className={classes["headingSide"]}>
					<div className={classes["badges"]}>
						<span>{ownershipLabels[ownership]}</span>
						{creditEnabled && <span data-kind="credit">À crédit</span>}
						{taxEnabled && <span data-kind="tax">Fiscalité estimée</span>}
					</div>
					{actions}
				</div>
			</div>
			<div className={classes["metrics"]}>
				{[
					["Investissement", money(r.invested), `${money(r.remaining)} disponibles`],
					["Revenus · année 1", money(first.income), `${money(first.income / 12)} / mois en moyenne`],
					[
						first.cashFlow < 0 ? "Effort mensuel · an 1" : "Excédent mensuel · an 1",
						money(Math.abs(first.cashFlow) / 12),
						taxEnabled ? "Après crédit et fiscalité" : "Après crédit, avant fiscalité",
					],
					["TRI annualisé", r.irr === null ? "Non calculable" : percent(r.irr), "Valeur finale et dette incluses"],
				].map(([label, value, caption], index) => (
					<div
						className={classes["metric"]}
						key={label}
						data-kind={index === 0 ? "capital" : index === 1 ? "income" : index === 2 ? (first.cashFlow < 0 ? "effort" : "surplus") : "irr"}
						data-long-value={value!.length > 13}>
						<span>{label}</span>
						<strong>{value}</strong>
						<small>{caption}</small>
					</div>
				))}
			</div>
			<div className={classes["resultTabs"]}>
				<NavTabComponent
					compact
					items={[
						{
							label: "Projection",
							children: (
								<>
									{" "}
									<section className={classes["card"]}>
										<Typography typo={ITypo.P_MEDIUM_BOLD} component={ITypoComponent.H2}>
											Projection sur {r.years.length} ans
										</Typography>
										<figure className={classes["chart"]}>
											<TooltipElement
												describeChild
												open={tooltipOpen}
												onOpen={() => setTooltipOpen(true)}
												onClose={() => setTooltipOpen(false)}
												enterDelay={0}
												enterTouchDelay={0}
												leaveTouchDelay={5000}
												placement="top"
												title={
													<span className={classes["chartTooltip"]}>
														<strong>Année {highlighted.year}</strong>
														<span>
															Valeur de sortie estimée <b>{money(highlighted.asset)}</b>
														</span>
														<span>
															Capital restant dû <b>{money(highlighted.debt)}</b>
														</span>
														<span>
															Capital net <b>{money(highlighted.asset - highlighted.debt)}</b>
														</span>
													</span>
												}>
												<svg
													ref={setChartElement}
													viewBox={`0 0 ${chartWidth} 190`}
													role="img"
													tabIndex={0}
													onMouseMove={(e) => selectAtPointer(e.clientX, e.clientY)}
													onTouchStart={(e) => {
														const touch = e.touches[0];
														if (touch) selectAtPointer(touch.clientX, touch.clientY);
													}}
													onFocus={() => setTooltipOpen(true)}
													onBlur={() => setTooltipOpen(false)}
													onKeyDown={(e) => {
														if (e.key === "Escape") {
															setTooltipOpen(false);
															return;
														}
														const next =
															e.key === "ArrowRight"
																? yearIndex + 1
																: e.key === "ArrowLeft"
																	? yearIndex - 1
																	: e.key === "Home"
																		? 0
																		: e.key === "End"
																			? r.years.length - 1
																			: null;
														if (next !== null) {
															e.preventDefault();
															setActiveYear(Math.max(0, Math.min(r.years.length - 1, next)));
															setTooltipOpen(true);
														}
													}}
													aria-label="Projection du capital et de la dette. Survolez ou touchez le graphique, ou utilisez les flèches gauche et droite pour parcourir les années. Tableau annuel disponible dans l’onglet dédié.">
													<defs>
														<linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
															<stop offset="0%" stopColor="var(--Success-700)" stopOpacity="0.2" />
															<stop offset="100%" stopColor="var(--Success-700)" stopOpacity="0.02" />
														</linearGradient>
													</defs>
													{[0, 0.5, 1].map((fraction) => (
														<g key={fraction}>
															<line
																x1="70"
																x2={chartWidth - 20}
																y1={145 - fraction * 110}
																y2={145 - fraction * 110}
																stroke="var(--Wild-Sand-100, #efefef)"
															/>
															<text x="58" y={149 - fraction * 110} textAnchor="end">
																{new Intl.NumberFormat("fr-FR", { notation: "compact", maximumFractionDigits: 1 }).format(maxAsset * fraction)} €
															</text>
														</g>
													))}
													<polygon points={`70,145 ${points("asset")} ${r.years.length > 1 ? chartWidth - 20 : 70},145`} fill={`url(#${gradientId})`} />
													<polyline points={points("asset")} fill="none" stroke="var(--Success-700)" strokeWidth="3" />
													<polyline points={points("debt")} fill="none" stroke="var(--Error-700)" strokeWidth="2" strokeDasharray="5 5" />
													{r.years.map((y, i) => (
														<circle
															key={y.year}
															cx={70 + (i / Math.max(1, r.years.length - 1)) * (chartWidth - 90)}
															cy={145 - (y.asset / maxAsset) * 110}
															r="4"
															fill="var(--Success-700)"
														/>
													))}
													{tooltipOpen && (
														<g aria-hidden="true" pointerEvents="none">
															<line x1={highlightedX} x2={highlightedX} y1="25" y2="145" stroke="var(--Opium-400, #bfa7a9)" strokeDasharray="3 3" />
															{(["asset", "debt"] as const).map((key) => (
																<circle
																	key={key}
																	cx={highlightedX}
																	cy={145 - (highlighted[key] / maxAsset) * 110}
																	r="5"
																	fill="white"
																	stroke={key === "asset" ? "var(--Success-700)" : "var(--Error-700)"}
																	strokeWidth="2"
																/>
															))}
														</g>
													)}

													<text x="70" y="172">
														Année 1
													</text>
													<text x={chartWidth - 20} y="172" textAnchor="end">
														Année {last.year}
													</text>
												</svg>
											</TooltipElement>
											<figcaption>
												<span>Valeur de sortie estimée</span>
												<span>Capital restant dû</span>
											</figcaption>
										</figure>
										<div className={classes["terminal"]}>
											<span>
												Valeur de sortie estimée<strong>{money(last.asset)}</strong>
											</span>
											<span>
												Dette restante<strong>{money(last.debt)}</strong>
											</span>
											<span>
												Capital net à terme<strong>{money(last.asset - last.debt)}</strong>
											</span>
										</div>
									</section>
								</>
							),
						},
						{
							label: "Tableau annuel",
							children: (
								<>
									<MuiTable
										heightMode="content"
										columnsHead={[
											["year", "Année"],
											["income", "Revenus"],
											["tax", "Impôts + IFI"],
											["debtPayments", "Crédit + assurance"],
											["cashFlow", "Flux net"],
											["debt", "Dette restante"],
											["asset", "Valeur de sortie"],
										].map(([key, headContent]) => ({ key: key!, headContent: headContent! }))}
										rows={r.years.map((y) => ({
											rowKey: String(y.year),
											year: y.year,
											income: money(y.income),
											tax: money(y.tax + y.ifi),
											debtPayments: money(y.debtPayments),
											cashFlow: money(y.cashFlow),
											debt: money(y.debt),
											asset: money(y.asset),
										}))}
									/>
								</>
							),
						},
						{
							label: "Répartition",
							children: (
								<>
									{" "}
									<div className={classes["twoColumns"]}>
										{(
											[
												["geography", "Répartition géographique"],
												["sectors", "Répartition sectorielle"],
											] as const
										).map(([key, label]) => (
											<section className={classes["card"]} key={key} data-kind={key}>
												<Typography typo={ITypo.P_MEDIUM_BOLD} component={ITypoComponent.H2}>
													{label}
												</Typography>
												<Typography typo={ITypo.P_SMALL}>{exposureNote}</Typography>
												{r[key].map((e) => (
													<div className={classes["exposure"]} key={e.label}>
														<div>
															<span>{e.label}</span>
															<span>{percent(e.weight)}</span>
														</div>
														<TooltipElement
															describeChild
															title={`${e.label} : ${percent(e.weight)} du montant investi`}
															enterDelay={0}
															enterTouchDelay={0}>
															<meter min={0} max={1} value={e.weight} aria-label={e.label} aria-valuetext={percent(e.weight)} tabIndex={0} />
														</TooltipElement>
													</div>
												))}
											</section>
										))}
									</div>
								</>
							),
						},
						{
							label: `Détails${r.warnings.length ? ` (${r.warnings.length})` : ""}`,
							children: (
								<div className={classes["fields"]}>
									<Typography typo={ITypo.P_SMALL}>
										Revenus annuels sans délai de jouissance : {money(r.annualRunRate)} ({percent(r.annualRunRate / r.invested)} du montant investi). Revenus
										nets de première année : {money(first.netIncome)}. Apport : {money(r.contribution)}. Valeur finale nette de dette :{" "}
										{money(last.asset - last.debt)}.
									</Typography>
									<section className={classes["card"]}>
										<Typography typo={ITypo.P_MEDIUM_BOLD} component={ITypoComponent.H2}>
											Allocation retenue
										</Typography>
										<MuiTable
											heightMode="content"
											columnsHead={[
												{ key: "name", headContent: "SCPI" },
												{ key: "shares", headContent: "Parts" },
												{ key: "unitCost", headContent: "Prix du droit" },
												{ key: "invested", headContent: "Investissement" },
												{ key: "rate", headContent: "Taux retenu" },
												{ key: "income", headContent: "Revenus année 1" },
											]}
											rows={r.lines.map((l) => ({
												rowKey: l.productId,
												name: l.name,
												shares: l.shares,
												unitCost: money(l.unitCost),
												invested: money(l.invested),
												rate: `${l.distributionRateSource === "net" ? "Net" : "Brut"} ${percent(l.distributionRate / 100)}`,
												income: money(l.firstYearIncome),
											}))}
										/>
										<Typography typo={ITypo.P_MEDIUM}>
											Apport initial : {money(r.contribution)} · Valeur finale estimée, dette déduite : {money(last.asset - last.debt)}
										</Typography>
									</section>
									<div className={classes["stack"]}>
										<Typography typo={ITypo.P_MEDIUM}>{feesNote(feeAmount(r.fees.subscription), r.years.length)}</Typography>
										{r.lines.map((line) => {
											const product = products.find((p) => p.productId === line.productId);
											const amounts = [line.fees.subscription.amount, line.fees.ongoing.amount, line.fees.transaction.amount];
											const total = amounts.some((amount) => amount === null) ? null : amounts.reduce<number>((sum, amount) => sum + amount!, 0);
											return (
												<section className={classes["feeCard"]} key={line.productId}>
													<Typography typo={ITypo.P_MEDIUM_BOLD} component={ITypoComponent.H2}>
														{line.name}
													</Typography>
													<Typography typo={ITypo.P_SMALL}>
														Revenus calculés au taux {line.distributionRateSource === "net" ? "net" : "brut"} de {percent(line.distributionRate / 100)}.
													</Typography>
													<table className={classes["feeTable"]}>
														<thead>
															<tr>
																<th scope="col">Frais</th>
																<th scope="col">Taux</th>
																<th scope="col">Montant sur la durée</th>
															</tr>
														</thead>
														<tbody>
															{(
																[
																	["Souscription, inclus dans le prix", line.fees.subscription, true],
																	["Courants, par an", line.fees.ongoing, false],
																	["Transaction, par an", line.fees.transaction, false],
																] as const
															).map(([label, fee, subscription]) => (
																<tr key={label}>
																	<th scope="row">{label}</th>
																	<td>{feeRate(fee.rate, subscription)}</td>
																	<td>{feeAmount(fee.amount)}</td>
																</tr>
															))}
															<tr className={classes["feeTotal"]}>
																<th scope="row">Total indicatif</th>
																<td> </td>
																<td>{feeAmount(total)}</td>
															</tr>
														</tbody>
													</table>
													<Typography typo={ITypo.P_SMALL}>
														Valeur de reconstitution :{" "}
														{product?.simulatorData?.reconstitutionValue == null ? "Non renseignée" : money(product.simulatorData.reconstitutionValue)}{" "}
														· Date de la source : {product?.simulatorData?.sourceDate ?? "Non renseignée"}
													</Typography>
												</section>
											);
										})}
										<section className={classes["feeCard"]}>
											<Typography typo={ITypo.P_MEDIUM_BOLD} component={ITypoComponent.H2}>
												Total de la simulation
											</Typography>
											<table className={classes["feeTable"]}>
												<tbody>
													<tr>
														<th scope="row">Souscription</th>
														<td>{feeAmount(r.fees.subscription)}</td>
													</tr>
													<tr>
														<th scope="row">Courants</th>
														<td>{feeAmount(r.fees.ongoing)}</td>
													</tr>
													<tr>
														<th scope="row">Transaction</th>
														<td>{feeAmount(r.fees.transaction)}</td>
													</tr>
													<tr className={classes["feeTotal"]}>
														<th scope="row">Total indicatif</th>
														<td>{feeAmount(r.fees.total)}</td>
													</tr>
												</tbody>
											</table>
										</section>
									</div>{" "}
									<MessageBox type="info" text={projectionDisclaimer} />
									{r.warnings.map((warning) => (
										<MessageBox key={warning} type="warning" text={warning} />
									))}
								</div>
							),
						},
					]}
				/>
			</div>
		</div>
	);
}
